import { z } from 'zod';

export const quarterMap = {
  Q1: ['01', '02', '03'],
  Q2: ['04', '05', '06'],
  Q3: ['07', '08', '09'],
  Q4: ['10', '11', '12'],
} as const;
export const quarterToStartingMonthIndexMap = {
  Q1: 1,
  Q2: 4,
  Q3: 7,
  Q4: 10,
} as const;
export type Quarter = keyof typeof quarterMap;
export const quarters = Object.keys(quarterMap) as Quarter[];

export type FirstMoment =
  | {
      type: 'year';
      year: string;
    }
  | {
      type: 'quarter';
      year: string;
      quarter: Quarter;
    }
  | {
      type: 'month';
      year: string;
      month: Month;
    }
  | {
      type: 'day';
      year: string;
      month: Month;
      day: Day;
    }
  | {
      type: 'hour';
      year: string;
      month: Month;
      day: Day;
      hour: Hour;
    };

export type Period =
  | {
      type: 'year';
      year: string;
    }
  | {
      type: 'quarter';
      year: string;
      quarter: Quarter;
    }
  | {
      type: 'month';
      year: string;
      month: Month;
    }
  | {
      type: 'day';
      year: string;
      month: Month;
      day: Day;
    }
  | {
      type: 'hour';
      year: string;
      month: Month;
      day: Day;
      hour: Hour;
    };

export const hours = [
  '00',
  '01',
  '02',
  '03',
  '04',
  '05',
  '06',
  '07',
  '08',
  '09',
  '10',
  '11',
  '12',
  '13',
  '14',
  '15',
  '16',
  '17',
  '18',
  '19',
  '20',
  '21',
  '22',
  '23',
] as const;

export type HourIndex =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 19
  | 20
  | 21
  | 22
  | 23;

export type Hour = (typeof hours)[number];

// type guard for hour
export const isHour = (hour: string): hour is Hour => {
  return hours.includes(hour as Hour);
};

export const days = [
  '01',
  '02',
  '03',
  '04',
  '05',
  '06',
  '07',
  '08',
  '09',
  '10',
  '11',
  '12',
  '13',
  '14',
  '15',
  '16',
  '17',
  '18',
  '19',
  '20',
  '21',
  '22',
  '23',
  '24',
  '25',
  '26',
  '27',
  '28',
  '29',
  '30',
  '31',
] as const;

export type Day = (typeof days)[number];

// type guard for day
export const isDay = (day: string): day is Day => {
  return days.includes(day as Day);
};

export type DayIndex =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 19
  | 20
  | 21
  | 22
  | 23
  | 24
  | 25
  | 26
  | 27
  | 28
  | 29
  | 30
  | 31;

export const months = [
  '01',
  '02',
  '03',
  '04',
  '05',
  '06',
  '07',
  '08',
  '09',
  '10',
  '11',
  '12',
] as const;

export type Month = (typeof months)[number];

export type MonthIndex = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

// type guard for month
export const isMonth = (month: string): month is Month => {
  return months.includes(month as Month);
};

export const MonthSchema = z.enum(months);

export const YearSchema = z
  .string()
  .regex(/^\d{4}$/, 'Expected a 4-digit year, e.g. "2026"');

// A `Period` narrowed to the granularities supported by date filtering. Days
// (use `equalTo`), quarters, and hours are rejected by the `PeriodFilter`
// scalar before a value of this type is produced.
export type PeriodFilter = Extract<
  Period,
  { type: 'year' } | { type: 'month' }
>;

export const PeriodFilterSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('year'), year: YearSchema }).strict(),
  z
    .object({ type: z.literal('month'), year: YearSchema, month: MonthSchema })
    .strict(),
]) satisfies z.ZodType<PeriodFilter>;

export type LastMoment =
  | {
      type: 'year';
      year: string;
    }
  | {
      type: 'month';
      year: string;
      month: Month;
    }
  | {
      type: 'day';
      year: string;
      month: Month;
      day: Day;
    }
  | {
      type: 'hour';
      year: string;
      month: Month;
      day: Day;
      hour: (typeof hours)[number];
    };
